import React from 'react'
import axios from 'axios'
import {
  MdFamilyRestroom,
  MdHourglassTop,
  MdMap,
  MdMusicNote,
  MdOutlineSchool,
  MdPeopleAlt,
  MdRefresh,
  MdViewList,
} from 'react-icons/md'
import { getRequestHeaders } from '../api'
import { baseUrl } from '../assets/Animations'
import {
  buildWaitlistSummary,
  normalizeWaitlistSchoolId,
  WAITLIST_SCHOOL_NAMES,
} from './waitlistSummaryUtils'
import WaitlistMap from './WaitlistMap'
import './waitlist-summary.css'

const DEFAULT_SCHOOLS = Object.entries(WAITLIST_SCHOOL_NAMES).map(([id, label]) => ({
  id,
  label,
}))

const getResponseArray = (response, nestedKey) => {
  if (Array.isArray(response?.data)) return response.data
  return Array.isArray(response?.data?.[nestedKey]) ? response.data[nestedKey] : []
}

const WaitlistSummary = ({ userData }) => {
  const [summary, setSummary] = React.useState(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState('')
  const [teachers, setTeachers] = React.useState([])
  const [activeView, setActiveView] = React.useState('summary')

  const loadSummary = React.useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const waitlistResponse = await axios.get(`${baseUrl}/waitlist`, getRequestHeaders())
      const [instrumentsResult, schoolsResult, teachersResult] = await Promise.allSettled([
        axios.get(`${baseUrl}/inst`, getRequestHeaders()),
        axios.get(`${baseUrl}/schools`, getRequestHeaders()),
        axios.get(`${baseUrl}/teacher-database-active`, getRequestHeaders()),
      ])

      let families = getResponseArray(waitlistResponse, 'families')
      const instruments = instrumentsResult.status === 'fulfilled'
        ? getResponseArray(instrumentsResult.value, 'instruments')
        : []
      const loadedSchools = schoolsResult.status === 'fulfilled'
        ? getResponseArray(schoolsResult.value, 'schools')
        : []
      let schools = [...DEFAULT_SCHOOLS, ...loadedSchools]
      let activeTeachers = teachersResult.status === 'fulfilled'
        ? getResponseArray(teachersResult.value, 'teachers')
        : []

      const managerSchool = userData?.PermissionLevel === 'manager'
        ? normalizeWaitlistSchoolId(userData?.School)
        : ''

      if (managerSchool) {
        families = families.filter((family) => (
          normalizeWaitlistSchoolId(family?.school || family?.whichSchool) === managerSchool
        ))
        schools = schools.filter((school) => (
          normalizeWaitlistSchoolId(
            school?.value || school?.abbreviation || school?.school || school?.id
          ) === managerSchool
        ))
        activeTeachers = activeTeachers.filter((teacher) => (
          normalizeWaitlistSchoolId(teacher?.School || teacher?.school) === managerSchool
        ))
      }

      setSummary(buildWaitlistSummary({ families, instruments, schools }))
      setTeachers(activeTeachers)
    } catch (requestError) {
      console.error('Unable to load waitlist summary:', requestError)
      setError('Please check your connection and try again.')
    } finally {
      setLoading(false)
    }
  }, [userData?.PermissionLevel, userData?.School])

  React.useEffect(() => {
    loadSummary()
  }, [loadSummary])

  return (
    <main className='waitlist-summary-page'>
      <div className='waitlist-summary-page__title-row'>
        <div>
          <span className='waitlist-summary-page__eyebrow'>Students</span>
          <h1>Waitlist</h1>
          <p>Students waiting for lesson placement, organized by school and instrument.</p>
        </div>
        <div className='waitlist-summary-page__actions'>
          <div className='waitlist-summary-page__view-toggle' aria-label='Waitlist view'>
            <button
              type='button'
              className={activeView === 'summary' ? 'is-active' : ''}
              aria-pressed={activeView === 'summary'}
              onClick={() => setActiveView('summary')}
            >
              <MdViewList size={17} aria-hidden='true' /> Summary
            </button>
            <button
              type='button'
              className={activeView === 'map' ? 'is-active' : ''}
              aria-pressed={activeView === 'map'}
              disabled={loading || Boolean(error)}
              onClick={() => setActiveView('map')}
            >
              <MdMap size={17} aria-hidden='true' /> Map
            </button>
          </div>
          <button
            type='button'
            className='waitlist-summary-page__refresh'
            onClick={loadSummary}
            disabled={loading}
          >
            <MdRefresh size={18} aria-hidden='true' />
            Refresh
          </button>
        </div>
      </div>

      {activeView === 'map' && !loading && !error ? (
        <WaitlistMap summary={summary} teachers={teachers} />
      ) : (
      <section className='waitlist-summary' aria-labelledby='waitlist-summary-title'>
        <header className='waitlist-summary__header'>
          <span className='waitlist-summary__icon' aria-hidden='true'>
            <MdHourglassTop size={25} />
          </span>
          <div>
            <h2 id='waitlist-summary-title'>Waitlist Summary</h2>
            <p>Family-level and individual student waitlists are included.</p>
          </div>
        </header>

        {loading ? (
          <div className='waitlist-summary__state' role='status'>
            <span className='waitlist-summary__spinner' />
            Gathering waitlisted students…
          </div>
        ) : error ? (
          <div className='waitlist-summary__state waitlist-summary__state--error' role='alert'>
            <strong>We couldn’t load the waitlist.</strong>
            <span>{error}</span>
            <button type='button' onClick={loadSummary}>
              <MdRefresh size={17} aria-hidden='true' /> Try again
            </button>
          </div>
        ) : (
          <div className='waitlist-summary__content'>
            <div className='waitlist-summary__totals'>
              <div className='waitlist-summary__total-card is-students'>
                <MdPeopleAlt size={22} aria-hidden='true' />
                <div>
                  <strong>{summary?.totalStudents || 0}</strong>
                  <span>Students waiting</span>
                </div>
              </div>
              <div className='waitlist-summary__total-card is-families'>
                <MdFamilyRestroom size={22} aria-hidden='true' />
                <div>
                  <strong>{summary?.totalFamilies || 0}</strong>
                  <span>Families represented</span>
                </div>
              </div>
              <div className='waitlist-summary__total-card is-schools'>
                <MdOutlineSchool size={22} aria-hidden='true' />
                <div>
                  <strong>{summary?.schoolsWithStudents || 0}</strong>
                  <span>School locations</span>
                </div>
              </div>
              <div className='waitlist-summary__total-card is-instruments'>
                <MdMusicNote size={22} aria-hidden='true' />
                <div>
                  <strong>{summary?.totalInstrumentPlacements || 0}</strong>
                  <span>Instrument needs</span>
                </div>
              </div>
            </div>

            {(summary?.totalStudents || 0) === 0 ? (
              <div className='waitlist-summary__empty'>
                <MdHourglassTop size={28} aria-hidden='true' />
                <strong>No students are currently on a waitlist.</strong>
              </div>
            ) : (
              <div className='waitlist-summary__schools'>
                {(summary?.schools || []).map((school) => (
                  <article
                    className={`waitlist-summary__school ${school.totalStudents === 0 ? 'is-empty' : ''}`}
                    key={school.id}
                  >
                    <div className='waitlist-summary__school-header'>
                      <div>
                        <span>{school.code || school.id.toUpperCase()}</span>
                        <h3>{school.name}</h3>
                        <small>
                          {school.totalFamilies} {school.totalFamilies === 1 ? 'family' : 'families'}
                        </small>
                      </div>
                      <div className='waitlist-summary__school-count'>
                        <strong>{school.totalStudents}</strong>
                        <span>{school.totalStudents === 1 ? 'student' : 'students'}</span>
                      </div>
                    </div>

                    {school.instruments.length === 0 ? (
                      <div className='waitlist-summary__empty-school'>No waitlisted students</div>
                    ) : (
                      <div className='waitlist-summary__instrument-list'>
                        {school.instruments.map((instrument) => (
                          <div className='waitlist-summary__instrument' key={instrument.name}>
                            <div className='waitlist-summary__instrument-heading'>
                              <span className='waitlist-summary__music-icon' aria-hidden='true'>
                                <MdMusicNote size={15} />
                              </span>
                              <strong>{instrument.name}</strong>
                              <span className='waitlist-summary__instrument-count'>{instrument.count}</span>
                            </div>
                            <div className='waitlist-summary__student-list'>
                              {instrument.students.map((student) => (
                                <div
                                  className='waitlist-summary__student'
                                  key={`${instrument.name}-${student.studentId}`}
                                >
                                  <strong>{student.studentName}</strong>
                                  {student.familyName && (
                                    <span>{student.familyName} family</span>
                                  )}
                                  <small>
                                    {student.city
                                      ? `${student.city}${student.state ? `, ${student.state}` : ''}`
                                      : 'Town not listed'}
                                  </small>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </article>
                ))}
              </div>
            )}
          </div>
        )}
      </section>
      )}
    </main>
  )
}

export default WaitlistSummary
